export default ({open}) => ({
    open: open,
    media: [],
    slideOverOpened: false,
    staticAbsoluteIndex: 0,
    firstMediaSelected: 0,
    absoluteIndex: 0,
    relativeIndex: 0,
    videoPlaying: false,
    onTimeUpdateListener: null,
    mediaId: null,
    videoProgress: 0,
    lastTouchY: 0,
    mediaInfoHeight: '40%',
    olHeight: '60%',
    minOlHeight: 20,
    minMediaInfoHeight: 40,
    maxMediaInfoHeight: 80,
    maxOlHeight: 60,
    hasNext: false,
    hasPrev: false,
    prevPage: null,
    nextPage: null,
    max: 0,
    rendering: 'memory',
    isFetchingPrevMedias: false,
    isFetchingNextMedias: false,
    bufferPrev: [],
    bufferNext: [],
    get isRenderingSsr() {
        return this.rendering === 'ssr';
    },
    get currentIndex() {
        const index = !this.isRenderingSsr ? this.relativeIndex : this.absoluteIndex;

        return index + 1;
    },
    get isFirstMedia() {
        return this.currentMedia?.id === this.firstFiveIdsFromMedias[0];
    },
    get isLastMedia() {
        return this.currentMedia?.id === this.lastFiveIdsFromMedias[this.lastFiveIdsFromMedias.length - 1];
    },
    get firstFiveIdsFromMedias() {
        return this.media.slice(0, 5).map(({id}) => id);
    },
    get lastFiveIdsFromMedias() {
        return this.media.slice(-5).map(({id}) => id);
    },
    get currentMedia() {
        return this.media[this.relativeIndex] || null;
    },
    get currentMediaIsFilled() {
        return this.currentMedia !== null;
    },
    get isPrevButtonDisabled() {
        if (this.slideOverOpened) {
            return true;
        }

        if (!this.isRenderingSsr) {
            return this.isFirstMedia;
        }

        return this.isFirstMedia && this.bufferPrev.length < 1;
    },
    get isNextButtonDisabled() {
        if (this.slideOverOpened) {
            return true;
        }

        if (!this.isRenderingSsr) {
            return this.isLastMedia;
        }

        return this.isLastMedia && this.bufferNext.length < 1;
    },
    get canShowLoadingPrevButton() {
        return this.isFetchingPrevMedias && this.isFirstMedia;
    },
    get canShowLoadingNextButton() {
        return this.isFetchingNextMedias && this.isLastMedia;
    },
    guessFetchDirection(id) {
        if (this.isRenderingSsr && this.canFetchNext(id)) {
            return 'next';
        }

        if (this.isRenderingSsr && this.canFetchPrev(id)) {
            return 'prev'
        }

        return '';
    },
    canFetchMore(id) {
        if (!this.isRenderingSsr) {
            return false;
        }

        return this.canFetchPrev(id) || this.canFetchNext(id);
    },
    canFetchNext(id) {
        if (!this.isRenderingSsr) {
           return false;
        }

        return this.lastFiveIdsFromMedias.includes(id) && this.hasNext;
    },
    canFetchPrev(id) {
        if (!this.isRenderingSsr) {
            return false;
        }

        return this.firstFiveIdsFromMedias.includes(id) && this.hasPrev;
    },
    close() {
        document.body.classList.remove('overflow-hidden');

        this.open = false;
        this.slideOverOpened = false;

        if (this.$refs.slider) {
            this.$refs.slider.scrollLeft = 0;
        }

        this.restartVideo();
    },
    openMediaInfoSlideOver() {
        this.slideOverOpened = true;
    },
    closeMediaInfoSlideOver() {
        this.slideOverOpened = false;
    },
    to(strategy) {
        return new Promise((resolve) => {
            const slider = this.$refs.slider;
            const sliderItem = slider.querySelector('li.slider-item');
            const current = slider.scrollLeft;
            const offset = sliderItem.getBoundingClientRect().width;

            if (this.isRenderingSsr) {
                this.handleFetchMore(slider)
            }

            this.resetElementsHeight();

            slider.scrollTo({
                left: strategy(current, offset),
                behavior: 'smooth'
            });

            setTimeout(() => resolve(), 500);
        });
    },
    async attachPrevBuffer() {
        this.scrolling = true;
        this.isFetchingPrevMedias = true;

        await this.delay(700, () => {
            const currentMedia = structuredClone(Alpine.raw(this.currentMedia));

            this.media.unshift(...this.bufferPrev[0]);

            this.relativeIndex = this.media.findIndex(media => media.id === currentMedia.id);

            this.scrolling = false;
            this.isFetchingPrevMedias = false;
            this.bufferPrev = [];
        });
    },
    async attachNextBuffer() {
        this.scrolling = true;
        this.isFetchingNextMedias = true;

        await this.delay(700, () => {
            this.media.push(...this.bufferNext[0]);

            this.scrolling = false;
            this.isFetchingNextMedias = false;
            this.bufferNext = [];
        });
    },
    async prev() {
        if (this.isFirstMedia && this.bufferPrev.length > 0 && !this.isFetchingPrevMedias) {
            await this.attachPrevBuffer();
        }

        if (this.slideOverOpened || this.isPrevButtonDisabled) {
            return;
        }

        if (this.scrolling) {
            this.restartVideo();
            return;
        }

        this.scrolling = true;

        if (this.isFirstMedia) {
            this.scrolling = false;
            return;
        }

        this.$dispatch('carousel-scrolling');

        this.relativeIndex -= 1;
        this.absoluteIndex -= 1;

        this.restartVideo();

        this.to((current, offset) => current - offset)
            .then(() => this.scrolling = false);
    },
    async next() {
        if (this.isLastMedia && this.bufferNext.length > 0 && !this.isFetchingNextMedias) {
            await this.attachNextBuffer();
        }

        if (this.slideOverOpened || this.isNextButtonDisabled) {
            return;
        }

        if (this.scrolling) {
            this.restartVideo();
            return;
        }

        this.scrolling = true;

        if (this.isLastMedia) {
            this.scrolling = false;
            return;
        }

        this.$dispatch('carousel-scrolling');

        this.relativeIndex += 1;
        this.absoluteIndex += 1;

        this.restartVideo();

        this.to((current, offset) => current + offset)
            .then(() => this.scrolling = false);
    },
    startVideo(mediaId) {
        if (this.scrolling) {
            return;
        }

        this.mediaId = mediaId;

        const video = document.querySelector(`#video-${mediaId}`);

        this.onTimeUpdateListener = () => {
            if (isNaN(video.duration)) {
                this.videoProgress = 0;
            } else {
                this.videoProgress = (video.currentTime / video.duration) * 100;
            }
        }

        video.addEventListener('timeupdate', this.onTimeUpdateListener.bind(this));

        video.play();

        this.videoPlaying = true;
    },
    pauseVideo(mediaId) {
        this.mediaId = mediaId;

        const video = document.querySelector(`#video-${mediaId}`);

        if (!video) {
            return;
        }

        video.pause();

        video.removeEventListener('timeupdate', this.onTimeUpdateListener);

        this.videoPlaying = false;
    },
    restartVideo() {
        const video = document.querySelector(`#video-${this.mediaId}`);

        if (!video) {
            return;
        }

        video.pause();
        video.currentTime = 0;
        video.load();

        video.removeEventListener('timeupdate', this.onTimeUpdateListener);

        this.videoProgress = 0;
        this.videoPlaying = false;
        this.mediaId = null;
    },
    mediaIsPhoto(media) {
        return media.type === 'photo';
    },
    mediaIsVideo(media) {
        return media.type === 'video';
    },
    resetElementsHeight({mediaInfoHeight = '40%', olHeight = '60%'} = {}) {
        if (window.innerWidth >= 1024) {
            olHeight = '86%';
        }

        this.mediaInfoHeight = mediaInfoHeight;
        this.olHeight = olHeight;

        const target = document.querySelector('.target');

        if (!target) {
            return;
        }

        this.$nextTick(() => {
            const {top} = target.getBoundingClientRect()

            this.centralizeNextAndPrevButtons(top);
        });
    },
    setupSsrProperties(event) {
        this.hasPrev = event.detail.hasPrev;
        this.hasNext = event.detail.hasNext;
        this.absoluteIndex = event.detail.absoluteIndex;
        this.prevPage = event.detail.prevPage;
        this.nextPage = event.detail.nextPage;
        this.firstMediaSelected = structuredClone(Alpine.raw(this.media[event.detail.relativeIndex]));
        this.staticAbsoluteIndex = event.detail.absoluteIndex;
    },
    clearMedias() {
        this.media = [];
    },
    handleFetchMore(slider) {
        const currentSliderItem = slider.querySelector(`#media-${this.currentMedia.id}`);

        const direction = currentSliderItem?.dataset?.direction ?? null;
        const fetchMore = currentSliderItem?.dataset?.fetchMore ?? null;

        if (!['next', 'prev'].includes(direction) || fetchMore !== "true") {
            return;
        }

        if (direction === 'next' && !this.isFetchingNextMedias && this.bufferNext.length < 1 && this.hasNext) {
            this.isFetchingNextMedias = true;
            this.$dispatch('medias::fetch-more', { currentPage: this.nextPage, direction });

            return;
        }

        if (direction === 'prev' && !this.isFetchingPrevMedias && this.bufferPrev.length < 1 && this.hasPrev) {
            this.isFetchingPrevMedias = true;
            this.$dispatch('medias::fetch-more', { currentPage: this.prevPage, direction });
        }
    },
    handleOpenCarousel(event) {
        document.body.classList.add('overflow-hidden');

        this.open = true;

        this.scrolling = true

        this.media = event.detail.media;
        this.relativeIndex = event.detail.relativeIndex;
        this.max = event.detail.total || this.media.length;

        this.rendering = event.detail.rendering || 'memory';
        this.absoluteIndex = this.relativeIndex;

        if (this.isRenderingSsr) {
            this.setupSsrProperties(event);
        }

        this.resetElementsHeight();

        this.$nextTick(() => {
            if (this.$refs.slider) {
                this.$refs.slider.scrollLeft = 0;
            }

            this
                .to(() => {
                    const element = document.querySelector(`#media-${this.currentMedia.id}`);

                    const {top} = document.querySelector('.target').getBoundingClientRect();

                    this.centralizeNextAndPrevButtons(top);

                    return element.getBoundingClientRect().left;
                })
                .then(() => this.scrolling = false);
        });
    },
    async handleAttachMedias(event) {
        const direction = event.detail.direction;
        const medias = event.detail.medias;
        const hasPrev = event.detail.hasPrev;
        const hasNext = event.detail.hasNext;

        this.hasPrev = hasPrev;
        this.hasNext = hasNext;

        if (direction === 'next') {
            medias.length > 0 && this.bufferNext.push(medias);

            this.isFetchingNextMedias = false;

            if (this.hasNext) {
                this.nextPage += 1;
            } else {
                this.nextPage = null;
            }

            if (this.isLastMedia && medias.length > 0) {
                await this.attachNextBuffer();
            }
        }

        if (direction === 'prev') {
            medias.length > 0 && this.bufferPrev.push(medias);

            this.isFetchingPrevMedias = false;

            if (this.hasPrev) {
                this.prevPage -= 1;
            } else {
                this.prevPage = null;
            }

            if (this.isFirstMedia && medias.length > 0) {
                await this.attachPrevBuffer();
            }
        }
    },
    handleMediaInfoTouchMove(event) {
        const currentTouchY = event.touches[0].clientY;
        const diff = this.lastTouchY - currentTouchY;

        const currentMediaInfoHeight = parseFloat(this.mediaInfoHeight);

        const newMediaInfoHeight = Math.min(
            Math.max(currentMediaInfoHeight + (diff * 0.1), this.minMediaInfoHeight),
            this.maxMediaInfoHeight
        );

        const newOlHeight = Math.max(100 - newMediaInfoHeight, this.minOlHeight);

        this.mediaInfoHeight = `${newMediaInfoHeight}%`;
        this.olHeight = `${newOlHeight}%`;

        this.lastTouchY = currentTouchY;
    },
    handleMediaInfoTouchStart(event) {
        this.lastTouchY = event.touches[0].clientY;
    },
    handleSliderScroll() {
        if (this.scrolling || window.innerWidth >= 768) {
            return;
        }

        this.$dispatch('carousel-scrolling');

        const slider = this.$refs.slider;
        const children = slider.querySelectorAll('.slider-item');

        children.forEach((child) => {
            const diffDistance = Math.abs(child.getBoundingClientRect().left - slider.getBoundingClientRect().left);
            const childIndex = parseInt(child.dataset.index);
            const mediaId = parseInt(child.id.replace(/\D+/g, ''));

            if (diffDistance < 10 && childIndex !== this.currentIndex - 1) {
                this.relativeIndex = childIndex;

                if (this.isRenderingSsr) {
                    const index01 = this.media.findIndex(media => media.id === this.firstMediaSelected.id);
                    const index02 = this.media.findIndex(media => media.id === mediaId);

                    if (index01 === -1 || index02 === -1) {
                        console.error('One or both IDs not found in the array');

                        return;
                    }

                    const distance = index02 - index01;

                    if (distance === 0) {
                        this.absoluteIndex = this.staticAbsoluteIndex;
                    } else if (distance >= 1) {
                        this.absoluteIndex = this.staticAbsoluteIndex + distance;
                    } else {
                        this.absoluteIndex = this.staticAbsoluteIndex - Math.abs(distance);
                    }

                    this.handleFetchMore(slider);
                }

                this.restartVideo();
                this.resetElementsHeight();
            }
        });
    },
    delay(ms, callback) {
        return new Promise((resolve) => {
            setTimeout(() => {
                callback();
                resolve();
            }, ms);
        });
    },
    centralizeNextAndPrevButtons(top) {
        document.querySelectorAll('#carousel-prev-btn-container')
            .forEach(el => el.style.top = `${top}px`);

        document.querySelectorAll('#carousel-next-btn-container')
            .forEach(el => el.style.top = `${top}px`);
    },
    init() {
        this.resetElementsHeight();

        const onResizeHandler = () => {
            if (window.innerWidth >= 1024) {
                this.slideOverOpened = false;
                this.resetElementsHeight();
            }

            const target = document.querySelector('.target');

            if (!target) {
                return;
            }

            const {top} = target.getBoundingClientRect();

            this.centralizeNextAndPrevButtons(top);
        }

        window.addEventListener('resize', onResizeHandler);

        window.addEventListener('beforeunload', () => {
            window.removeEventListener('resize', onResizeHandler);
        });

        window.addEventListener('livewire:navigate', () => {
            window.removeEventListener('resize', onResizeHandler);
        });
    }
});
